import * as React from "react";
import PathConstants from "../routes/pathConstants";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { listFeatures } from "../utils/Model";
import { useAuth } from "./Authentication";
import { useShortbread } from "../contexts/shortbread";
import SideNavigation, {
  SideNavigationProps,
} from "@amzn/awsui-components-console/side-navigation";
import { logger } from "../utils/LogUtils";
import { useFeatureFlag, FeatureFlag } from "../utils/featureFlags";
import Spinner from "@amzn/awsui-components-console/spinner";
import Badge from "@amzn/awsui-components-console/badge";
import {
  isAdminUser,
  isBuilderUser,
  isPartnerAdminUser,
  isPartnerUser,
} from "../utils/AuthUtils";
import { FeatureGet } from "../utils/types/feature";

/**
 * Loads features
 */
const loadFeatures = async (query: any) => {
  try {
    const user = query.queryKey[1];
    if (!user) {
      logger.info("SideNav.loadFeatures() no logged in user");
      return [];
    }

    return await listFeatures();
  } catch (error) {
    logger.error(`Failed to load features`, error);
    throw error;
  }
};

interface QueryResultProps {
  data: FeatureGet[];
  error: any;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Left hand side navigation component
 */
export default function SideNav() {
  const { user } = useAuth();
  const { customizeCookies } = useShortbread();
  const isDemoHistoryPlaysEnabled = useFeatureFlag(
    FeatureFlag.DEMO_HISTORY_PLAYS
  );

  const location = useLocation();

  const [activeHref, setActiveHref] = React.useState(location.pathname);

  React.useEffect(() => {
    if (location.pathname === "/") {
      setActiveHref("/");
    } else if (location.pathname === PathConstants.RELEASES) {
      setActiveHref(PathConstants.RELEASES);
    } else if (location.pathname === PathConstants.ROADMAP) {
      setActiveHref(PathConstants.ROADMAP);
    } else if (location.pathname === PathConstants.FEEDBACK) {
      setActiveHref(PathConstants.FEEDBACK);
    } else if (location.pathname.startsWith("/chat/")) {
      // For chat routes, highlight "Chat with me" unless it's the chat list
      if (location.pathname === PathConstants.CHAT_LIST) {
        setActiveHref(PathConstants.CHAT_LIST);
      } else {
        setActiveHref(PathConstants.CHAT_NEW);
      }
    }
  }, [location.pathname]);

  const navigate = useNavigate();

  const {
    data: features,
    error,
    isLoading,
    refetch,
  }: QueryResultProps = useQuery(["features", user], loadFeatures, {
    retry: 5,
    staleTime: 300000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    initialData: undefined,
    retryDelay: (attemptIndex, error) => {
      logger.info(`Retry: ${attemptIndex} cause: ${error.message}`);
      return 1000;
    },
  });

  React.useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const featureItemsErrorText = `Refresh the page`;
  const getNavItems = () => {
    const navItems: SideNavigationProps.Item[] = [
      { type: "link", text: `Login`, href: PathConstants.LOGIN },
    ];

    if (user) {
      navItems.length = 0;

      if (!isPartnerUser(user)) {
        navItems.push({
          type: "link",
          text: `Analytics dashboard`,
          href: PathConstants.DASHBOARD,
        });
      }

      navItems.push({
        type: "link",
        text: "My favorite demos",
        href: `/demos/favourites`,
      });

      navItems.push({
        type: "link",
        text: "Search all demos",
        href: PathConstants.DEMO_SEARCH_LIST,
      });

      if (!isPartnerUser(user)) {
        navItems.push({
          type: "link",
          text: "Demo creation process",
          href: PathConstants.DEMO_CREATION,
        });
      }

      if (isDemoHistoryPlaysEnabled && !isPartnerUser(user)) {
        navItems.push({
          type: "link",
          text: "Demo play history",
          href: PathConstants.DEMO_PLAY_HISTORY,
        });
      }

      if (!isPartnerUser(user)) {
        navItems.push({
          type: "section",
          text: "Chat Assistant",
          defaultExpanded: true,
          items: [
            {
              type: "link",
              text: "Demo Portal Assistant",
              href: PathConstants.CHAT_NEW,
            },
            {
              type: "link",
              text: "Chat history",
              href: PathConstants.CHAT_LIST,
            },
          ],
        });
      }

      const featureItems: SideNavigationProps.Item[] = [];
      if (features) {
        features.forEach((feature) => {
          featureItems.push({
            type: "link",
            text: `${feature.name}`,
            href: `/demos/${feature.featureId}`,
          });
        });
      } else if (isLoading) {
        featureItems.push({
          type: "link",
          text: "",
          href: "",
          info: <Spinner />,
        });
      } else if (error) {
        featureItems.push({
          type: "link",
          text: featureItemsErrorText,
          href: "",
          info: <Badge color="red">Error</Badge>,
        });
      }

      navItems.push({
        type: "section",
        text: "Featured demos",
        defaultExpanded: true,
        items: featureItems,
      });

      if (!isPartnerUser(user)) {
        navItems.push({
          type: "section",
          text: "What's new",
          defaultExpanded: true,
          items: [
            { type: "link", text: `Roadmap`, href: PathConstants.ROADMAP },
            { type: "link", text: `Releases`, href: PathConstants.RELEASES },
          ],
        });
      }

      navItems.push({
        type: "divider",
      });

      navItems.push({
        type: "link",
        text: "Give feedback",
        href: PathConstants.FEEDBACK,
      });

      if (
        isBuilderUser(user) ||
        isAdminUser(user) ||
        isPartnerAdminUser(user)
      ) {
        navItems.push({
          type: "divider",
        });
      }

      if (isBuilderUser(user)) {
        navItems.push({
          type: "section",
          text: "Builders",
          defaultExpanded: true,
          items: [{ type: "link", text: `View my demos`, href: "/demos/my" }],
        });
      }

      if (isAdminUser(user) || isPartnerAdminUser(user)) {
        navItems.push({
          type: "section",
          text: isAdminUser(user) ? "User management" : "Partner management",
          items: [
            {
              type: "link",
              text: isAdminUser(user) ? "Manage users" : "Manage partner users",
              href: PathConstants.USER_LIST,
            },
            {
              type: "link",
              text: `Manage domains`,
              href: PathConstants.PARTNER_DOMAIN_LIST,
            },
            {
              type: "link",
              text: `Approve registrations`,
              href: PathConstants.PARTNER_APPROVAL_LIST,
            },
          ],
        });
      }

      if (isAdminUser(user)) {
        navItems.push({
          type: "section",
          text: "Administration",
          items: [
            {
              type: "link",
              text: `Create a demo`,
              href: PathConstants.DEMO_TYPE_SELECT,
            },
            {
              type: "link",
              text: `View all demos`,
              href: PathConstants.DEMO_PROJECT_BOARD,
            },
            {
              type: "link",
              text: `Manage featured demos section`,
              href: PathConstants.FEATURE_LIST,
            },
            {
              type: "link",
              text: "View all feedback requests",
              href: PathConstants.FEEDBACK_LIST,
            },
            {
              type: "link",
              text: "Manage demo form options",
              href: PathConstants.ADMIN_DEMO_FORM_OPTIONS,
            },
            ...(isDemoHistoryPlaysEnabled
              ? [
                  {
                    type: "link" as const,
                    text: "Demo attribution analytics",
                    href: PathConstants.ADMIN_DEMO_ATTRIBUTION,
                  },
                ]
              : []),
          ],
        });
      }
    }

    navItems.push({
      type: "divider",
    });

    navItems.push({
      type: "link",
      text: "Cookie Preferences",
      href: "#cookie",
    });

    navItems.push({
      type: "link",
      text: "© 2025, Amazon Web Services, Inc. or its affiliates. All rights reserved.",
      href: "#copyright",
    });

    return navItems;
  };

  return (
    <SideNavigation
      key={"sideName"}
      activeHref={activeHref}
      header={{
        href: PathConstants.HOME,
        text: "Demo Portal",
      }}
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          if (event.detail.text === "Cookie Preferences") {
            customizeCookies();
            return;
          }
          if (
            !event.detail.href &&
            event.detail.text === featureItemsErrorText
          ) {
            window.location.reload();
          } else if (!event.detail.href) {
            return;
          }
          setActiveHref(event.detail.href);
          navigate(event.detail.href);
        }
      }}
      items={getNavItems()}
    />
  );
}
